import ListItemLoading from "@/components/loading/listItemLoading/listItemLoading";
import Header from "@/components/header";
import CustomerItem from "@/modules/customer/list/components/customerItem";
import { ActionIcon, TextInput } from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { IconSearch, IconUserSearch, IconUsers, IconX } from "@tabler/icons-react";
import { useEffect, useRef, useState } from "react";
import useGetSearchCustomers from "../hooks/useGetSearchCustomers";
import classes from "./search.module.css";

const minimumSearchLength = 3;

function SearchCustomerPage() {
  const inputRef = useRef<HTMLInputElement>(null);
  const [value, setValue] = useState("");
  const [debounced] = useDebouncedValue(value.trim(), 500);
  const canSearch = debounced.length >= minimumSearchLength;

  const { data, isLoading, refetch } = useGetSearchCustomers({
    params: { Count: 20, Skip: 0, Term: debounced },
  });

  useEffect(() => {
    if (canSearch) void refetch();
  }, [canSearch, debounced, refetch]);

  const results = canSearch ? data?.result ?? [] : [];
  const showMinimumHint = value.trim().length > 0 && value.trim().length < minimumSearchLength;
  const clearSearch = () => {
    setValue("");
    inputRef.current?.focus();
  };

  return (
    <main className={classes.page} dir="rtl">
      <Header title="جست‌وجوی مشتری" showBack route="/dashboard/customers" bg="light-gray.0" />
      <section className={classes.content}>
        <div className={classes.pageTitle}>
          <span><IconUserSearch size={22} /></span>
          <div><h1>جست‌وجوی مشتری</h1><p>مشتری موردنظرتان را سریع پیدا کنید</p></div>
        </div>
        <div className={classes.searchPanel}>
          <label htmlFor="customer-search">نام یا شماره موبایل</label>
          <p>حداقل ۳ حرف از نام یا ۳ رقم از شماره مشتری را وارد کنید.</p>
          <TextInput
            id="customer-search"
            ref={inputRef}
            autoFocus
            size="lg"
            value={value}
            onChange={(event) => setValue(event.currentTarget.value)}
            placeholder="مثلاً سارا یا ۰۹۱۲"
            leftSection={<IconSearch size={21} strokeWidth={1.8} />}
            rightSection={value ? <ActionIcon variant="subtle" color="gray" radius="xl" onClick={clearSearch} aria-label="پاک کردن جست‌وجو"><IconX size={18} /></ActionIcon> : null}
            classNames={{ input: classes.searchInput, section: classes.inputSection }}
            aria-describedby={showMinimumHint ? "search-hint" : undefined}
          />
          {showMinimumHint && <div id="search-hint" className={classes.hint}>برای شروع جست‌وجو، حداقل ۳ کاراکتر وارد کنید.</div>}
        </div>

        {isLoading ? (
          <div className={classes.loading}><ListItemLoading count={4} /></div>
        ) : canSearch && results.length > 0 ? (
          <section className={classes.results} aria-live="polite">
            <div className={classes.resultsHeader}>
              <h2>نتایج جست‌وجو</h2>
              <span>{results.length.toLocaleString("fa-IR")} مشتری پیدا شد</span>
            </div>
            <div className={classes.resultList}>
              {results.map((customer) => <div className={classes.resultCard} key={customer?.id}><CustomerItem customer={customer} /></div>)}
            </div>
          </section>
        ) : canSearch ? (
          <div className={classes.empty} aria-live="polite">
            <span><IconUserSearch size={32} strokeWidth={1.5} /></span>
            <h2>مشتری پیدا نشد</h2>
            <p>نام یا شماره موبایل را بررسی کنید و دوباره جست‌وجو کنید.</p>
            <button type="button" onClick={clearSearch}>جست‌وجوی دوباره</button>
          </div>
        ) : (
          <div className={classes.startState}>
            <span><IconUsers size={34} strokeWidth={1.5} /></span>
            <h2>مشتری‌تان را پیدا کنید</h2>
            <p>با جست‌وجوی نام یا شماره موبایل، مستقیماً وارد پرونده مشتری شوید.</p>
          </div>
        )}
      </section>
    </main>
  );
}

export default SearchCustomerPage;
